/**
 * Service Management Section
 * @description 서비스 관리 - services.json CRUD 기능
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@shared/ui/primitives';
import { getApiEndpoint } from '@shared/config';
import { Plus, Pencil, Trash2, CheckCircle2, ChevronUp, ChevronDown } from 'lucide-react';
import { AddServiceDialog } from '../components/AddServiceDialog';
import { EditServiceDialog, type ServiceConfig } from '../components/EditServiceDialog';

const SERVICES_API_URL = getApiEndpoint('/services');

export const PortalServicePage: React.FC = () => {
  const [services, setServices] = useState<ServiceConfig[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [availableIndexes, setAvailableIndexes] = useState<number[]>([]);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await fetch(SERVICES_API_URL);
      if (!response.ok) {
        throw new Error('Failed to load services from API');
      }
      const data = await response.json();
      setServices(data.services || []);
    } catch (error) {
      console.error('Error loading services:', error);
      alert('서비스 목록을 불러오는 중 오류가 발생했습니다.');
    }
  };

  const saveServices = async (newServices: ServiceConfig[]) => {
    setIsLoading(true);
    try {
      const normalized = newServices.map((s, i) => ({
        ...s,
        displayOrder: (i + 1) * 10,
      }));

      localStorage.setItem('iampam_portal_services_backup', JSON.stringify({ services: normalized }));

      const response = await fetch(SERVICES_API_URL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ services: normalized }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save services');
      }

      setServices(normalized);
    } catch (error) {
      console.error('Error saving services:', error);
      alert(`저장 중 오류가 발생했습니다: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAvailableIndexes = useCallback(() => {
    const usedIndexes = new Set<number>();
    services.forEach((service: ServiceConfig) => {
      if (service.ports?.frontend) {
        const index = service.ports.frontend - 4000;
        if (index >= 1 && index <= 100) {
          usedIndexes.add(index);
        }
      }
      if (service.ports?.backend) {
        const index = service.ports.backend - 5000;
        if (index >= 1 && index <= 100) {
          usedIndexes.add(index);
        }
      }
      if (service.ports?.['frontend-admin']) {
        const index = service.ports['frontend-admin'] - 6000;
        if (index >= 1 && index <= 100) {
          usedIndexes.add(index);
        }
      }
    });

    const available: number[] = [];
    for (let i = 1; i <= 100; i++) {
      if (!usedIndexes.has(i)) {
        available.push(i);
      }
    }
    return available;
  }, [services]);

  useEffect(() => {
    setAvailableIndexes(calculateAvailableIndexes());
  }, [services, calculateAvailableIndexes]);

  const handleAdd = useCallback(() => {
    setAvailableIndexes(calculateAvailableIndexes());
    setIsAddDialogOpen(true);
  }, [calculateAvailableIndexes]);

  const handleAddService = useCallback(async (serviceData: ServiceConfig) => {
    setIsLoading(true);
    try {
      const existingFolder = services.find((s) => s.folder === serviceData.folder.trim());
      if (existingFolder) {
        alert('이미 존재하는 폴더명입니다.');
        throw new Error('Duplicate folder');
      }

      const existingName = services.find((s) => s.name.trim() === serviceData.name.trim());
      if (existingName) {
        alert('이미 존재하는 서비스 이름입니다.');
        throw new Error('Duplicate name');
      }

      const response = await fetch(getApiEndpoint('/services/create'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(serviceData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create service');
      }

      await loadServices();
      setSuccessMessage(`Service "${serviceData.name}" has been created successfully.`);
      setIsSuccessDialogOpen(true);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      if (message !== 'Duplicate folder' && message !== 'Duplicate name') {
        alert(`Failed to create service: ${message}`);
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [services]);

  const isPortalService = (folder: string) => {
    return ['it-service-request', 'tenant-admin', 'admin'].includes(folder);
  };

  const isOrderableService = (folder: string) => !isPortalService(folder);

  const handleEdit = (index: number) => {
    const service = services[index];
    if (isPortalService(service.folder)) {
      alert('Portal 서비스는 편집할 수 없습니다.');
      return;
    }
    setEditingIndex(index);
  };

  const handleEditService = useCallback(
    async (updatedService: ServiceConfig) => {
      if (editingIndex === null) return;

      const hasFrontend = Number(updatedService.ports.frontend) > 0;
      const hasBackend = Number(updatedService.ports.backend) > 0;
      const hasAdmin = Number(updatedService.ports['frontend-admin']) > 0;

      const scaffoldResponse = await fetch(getApiEndpoint('/services/ensure-components'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folder: updatedService.folder,
          name: updatedService.name,
          description: updatedService.description,
          components: {
            backend: hasBackend,
            frontend: hasFrontend,
            'frontend-admin': hasAdmin,
          },
          ports: updatedService.ports,
        }),
      });

      if (!scaffoldResponse.ok) {
        const errorData = await scaffoldResponse.json().catch(() => ({}));
        throw new Error(
          (errorData as { message?: string }).message ||
            (errorData as { error?: string }).error ||
            'Failed to scaffold service components'
        );
      }

      const scaffoldResult = (await scaffoldResponse.json()) as {
        message?: string;
        created?: Record<string, boolean>;
      };

      const newServices = services.map((s, i) => (i === editingIndex ? updatedService : s));
      await saveServices(newServices);

      const createdParts = Object.entries(scaffoldResult.created ?? {})
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (createdParts.length > 0) {
        setSuccessMessage(
          `서비스가 저장되었습니다. 템플릿에서 생성된 컴포넌트: ${createdParts.join(', ')}`
        );
        setIsSuccessDialogOpen(true);
      }
    },
    [editingIndex, services]
  );

  const handleDelete = (index: number) => {
    const service = services[index];
    if (isPortalService(service.folder)) {
      alert('Portal 서비스는 삭제할 수 없습니다.');
      return;
    }
    setDeleteIndex(index);
    setIsDeleteDialogOpen(true);
  };

  const moveService = async (index: number, direction: 'up' | 'down') => {
    if (!isOrderableService(services[index].folder)) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= services.length) return;
    if (!isOrderableService(services[newIndex].folder)) return;
    const newServices = [...services];
    [newServices[index], newServices[newIndex]] = [newServices[newIndex], newServices[index]];
    await saveServices(newServices);
  };

  const confirmDelete = async () => {
    if (deleteIndex === null) return;
    const serviceToDelete = services[deleteIndex];
    const newServices = services.filter((_, i) => i !== deleteIndex);
    try {
      await saveServices(newServices);
      setIsDeleteDialogOpen(false);
      setDeleteIndex(null);
      alert(`Portal service "${serviceToDelete.name}" has been deleted successfully.`);
    } catch (error) {
      console.error('Error deleting service:', error);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Portal 서비스 관리</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Portal 서비스 설정 및 관리 (services.json CRUD)
          </p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="w-4 h-4 mr-2" />
          서비스 추가
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">순서</TableHead>
              <TableHead>폴더명</TableHead>
              <TableHead>서비스명</TableHead>
              <TableHead>설명</TableHead>
              <TableHead>라벨</TableHead>
              <TableHead>Frontend</TableHead>
              <TableHead>Backend</TableHead>
              <TableHead>Admin</TableHead>
              <TableHead className="text-right">작업</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {services.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                  서비스가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              services.map((service, index) => {
                const isPortal = isPortalService(service.folder);
                return (
                  <TableRow key={service.folder}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => moveService(index, 'up')}
                          disabled={index === 0 || isPortal || !isOrderableService(service.folder)}
                          title={isPortal ? 'Portal 서비스는 순서 변경 불가' : '위로 이동'}
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => moveService(index, 'down')}
                          disabled={
                            index === services.length - 1 ||
                            isPortal ||
                            !isOrderableService(service.folder) ||
                            !isOrderableService(services[index + 1]?.folder ?? '')
                          }
                          title={isPortal ? 'Portal 서비스는 순서 변경 불가' : '아래로 이동'}
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{service.folder}</TableCell>
                    <TableCell>{service.name}</TableCell>
                    <TableCell className="max-w-xs truncate">{service.description}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {service.labels.map((label, i) => (
                          <span
                            key={i}
                            className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded"
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{service.ports.frontend}</TableCell>
                    <TableCell>{service.ports.backend}</TableCell>
                    <TableCell>{service.ports['frontend-admin'] || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(index)}
                          disabled={isPortal}
                          title={isPortal ? 'Portal 서비스는 편집할 수 없습니다' : '편집'}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(index)}
                          disabled={isPortal}
                          title={isPortal ? 'Portal 서비스는 삭제할 수 없습니다' : '삭제'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {isAddDialogOpen && (
        <AddServiceDialog
          open
          onOpenChange={setIsAddDialogOpen}
          onSave={handleAddService}
          availableIndexes={availableIndexes}
        />
      )}

      {editingIndex !== null && (
        <EditServiceDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditingIndex(null);
          }}
          service={services[editingIndex]}
          existingServices={services}
          onSave={handleEditService}
        />
      )}

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-destructive" />
              Delete Portal Service
            </DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to delete this portal service? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteIndex !== null && (
            <div className="py-4">
              <div className="rounded-lg border border-border bg-muted/50 p-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">Service Name:</span>
                    <span className="text-sm font-semibold">{services[deleteIndex].name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">Folder:</span>
                    <span className="text-sm font-mono text-foreground">{services[deleteIndex].folder}</span>
                  </div>
                  {services[deleteIndex].description && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-muted-foreground">Description:</span>
                      <span className="text-sm text-foreground">{services[deleteIndex].description}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeleteIndex(null);
              }}
              disabled={isLoading}
              className="text-black"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={isLoading}
              className="gap-2 text-black"
            >
              <Trash2 className="w-4 h-4" />
              {isLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isSuccessDialogOpen} onOpenChange={setIsSuccessDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold">Success</DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground mt-1">
                  Operation completed successfully
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-foreground">{successMessage}</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setIsSuccessDialogOpen(false)} className="w-full">
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

PortalServicePage.displayName = 'PortalServicePage';
